import { type ReactElement, useCallback, useEffect, useState } from 'react';
import { type FoodPostsaleCaseView } from '@vello/api-client';
import { isIso4217Currency } from '@vello/core';
import { formatMoney } from '@vello/format';
import { Badge, Button, Drawer, EmptyState, ListTable, Notice, PageHeader, type Column, DrawerHeader, DrawerBody, DrawerFooter, SectionTitle, Text, Ink, Line, AUTO_REFRESH_MS, useAutoRefresh } from '@vello/web-theme';
import {
  maySelfApprove,
  POSTSALE_DECISION_REASONS,
  formatMarketInstant,
  mayDecidePostsale,
  mayManagePostsale,
  postsaleActions,
  postsaleActorsAreLegacy,
  postsaleTone,
  scopeTimeZone,
  tailOf,
} from '../../src/index';
import { useAdmin } from '../session';
import { useConsole } from '../console';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { DrawerStack } from '../components/drawer-stack';
import { IdRef } from '../components/code-ref';

export function FoodPostSaleScreen(): ReactElement {
  const { runtime, scope, markets, grants, me } = useAdmin();
  const selfApproval = maySelfApprove(grants); // D868: a GLOBAL super administrator decides their own proposals
  const { toast, confirm } = useConsole();
  const { t, lang } = useT();
  const { fail, v } = useVocab();
  const timeZone = scopeTimeZone(scope.current, markets);
  const mayDecide = mayDecidePostsale(grants);
  // Review W6N-06 (Codex P2): `submit` is `postsale:manage` on the server; `resolve`/`reject` are `postsale:decide`.
  const mayManage = mayManagePostsale(grants);

  const [cases, setCases] = useState<readonly FoodPostsaleCaseView[] | undefined>(undefined);
  const [listError, setListError] = useState<string | undefined>(undefined);
  const [open, setOpen] = useState<FoodPostsaleCaseView | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | undefined>(undefined);

  const money = useCallback(
    (m: { readonly amountMinor: string; readonly currency: string } | undefined): string => {
      if (m === undefined) return '—';
      return /^-?\d+$/.test(m.amountMinor) && isIso4217Currency(m.currency)
        ? formatMoney({ amountMinor: BigInt(m.amountMinor), currency: m.currency }, { locale: lang, timeZone: 'UTC', measurement: 'metric' })
        : `${m.amountMinor} ${m.currency}`;
    },
    [lang],
  );

  const load = useCallback(async (): Promise<void> => {
    const res = await runtime.foodPostsale.listOpen();
    if (res.ok) {
      setCases(res.value.cases);
      setListError(undefined);
    } else {
      setCases((prev) => prev ?? []);
      setListError(res.error.code === 'FORBIDDEN' ? t('w6.refused.forbidden') : t('w6.refused', { code: fail(res.error) }));
    }
  }, [runtime, t]);

  useEffect(() => {
    void load();
  }, [load]);
  useAutoRefresh(load, { intervalMs: AUTO_REFRESH_MS.queue });

  const openDetail = async (view: FoodPostsaleCaseView): Promise<void> => {
    setOpen(view);
    setActionError(undefined);
    const res = await runtime.foodPostsale.get(view.caseId);
    if (res.ok) setOpen(res.value.case);
  };

  /**
   * The reviewer/actor the api-client still takes as an argument is the ROLE the server re-derives from the session
   * (D397): `platform` is the only role a platform operator may claim, and the server refuses anything else.
   */
  const run = async (view: FoodPostsaleCaseView, action: 'submit' | 'resolve' | 'reject'): Promise<void> => {
    let reasonCode: string | undefined;
    if (action !== 'submit') {
      const answer = await confirm({
        title: t(action === 'resolve' ? 'fps.resolve.title' : 'fps.reject.title', { id: tailOf(view.caseId) }),
        body: t(action === 'resolve' ? 'fps.resolve.body' : 'fps.reject.body'),
        confirmLabel: t(action === 'resolve' ? 'fps.resolve.confirm' : 'fps.reject.confirm'),
        cancelLabel: t('ui.cancel'),
        tone: action === 'reject' ? 'danger' : 'primary',
        icon: action === 'reject' ? 'ban' : 'check',
        reasons: POSTSALE_DECISION_REASONS.map((code) => v('food.post_sale_decision_reason', code)),
        reasonLabel: t('w6.reason.label'),
        reasonsOnly: true,
      });
      if (!answer.confirmed || answer.reason === undefined) return;
      reasonCode = POSTSALE_DECISION_REASONS.find((code) => v('food.post_sale_decision_reason', code) === answer.reason);
      if (reasonCode === undefined) return;
    }
    setBusy(true);
    setActionError(undefined);
    const res =
      action === 'submit'
        ? await runtime.foodPostsale.submit(view.caseId, 'platform')
        : action === 'resolve'
          ? await runtime.foodPostsale.resolve(view.caseId)
          : await runtime.foodPostsale.reject(view.caseId, 'platform', reasonCode as string);
    setBusy(false);
    if (res.ok) {
      setOpen(res.value.case);
      toast({ message: t(action === 'submit' ? 'fps.submit.done' : action === 'resolve' ? 'fps.resolve.done' : 'fps.reject.done'), tone: 'success' });
      await load();
      return;
    }
    setActionError(t('w6.actionRefused', { code: fail(res.error) }));
  };

  const columns: readonly Column<FoodPostsaleCaseView>[] = [
    { key: 'case', header: t('fps.col.case'), width: 'minmax(110px, 0.9fr)', render: (c) => <IdRef value={c.caseId} /> },
    { key: 'order', header: t('fps.col.order'), width: 'minmax(110px, 0.9fr)', render: (c) => <IdRef value={c.orderRef} /> },
    { key: 'kind', header: t('fps.col.kind'), width: 'minmax(120px, 0.9fr)', render: (c) => v('food.post_sale_kind', c.kind) },
    { key: 'state', header: t('fps.col.state'), width: 'minmax(130px, 1fr)', render: (c) => <Badge tone={postsaleTone(c.state)}>{v('food.post_sale_state', c.state)}</Badge> },
    { key: 'opened', header: t('fps.col.opened'), width: 'minmax(140px, 1fr)', render: (c) => <Text role="caption" tone="secondary" mono>{formatMarketInstant(c.openedAt, timeZone, lang)}</Text> },
  ];

  const actions = open === undefined ? [] : postsaleActions(open, me?.id, selfApproval);

  return (
    <>
      <PageHeader title={t('fps.title')} subtitle={t('fps.subtitle')} />


      <ListTable
        columns={columns}
        rows={cases ?? []}
        rowKey={(c) => c.caseId}
        onRowClick={(c) => void openDetail(c)}
        selectedKey={open?.caseId}
        loading={cases === undefined}
        loadingLabel={t('ui.loading')}
        error={listError}
        onRetry={() => void load()}
        retryLabel={t('ui.retry')}
        empty={<EmptyState title={t('fps.empty.title')} detail={t('fps.empty.detail')} />}
        minWidth={900}
      />

      <Drawer open={open !== undefined} onClose={() => setOpen(undefined)} labelledBy="fps-detail-title">
        {open !== undefined && (
          <>
            <DrawerHeader titleId="fps-detail-title" title={t('fps.detail.title', { id: tailOf(open.caseId) })} subtitle={<Badge tone={postsaleTone(open.state)}>{v('food.post_sale_state', open.state)}</Badge>} />
            <DrawerBody>
              <DrawerStack>
                {postsaleActorsAreLegacy(open) && <Notice tone="warning">{t('w6.legacyActors')}</Notice>}
                {((actions.includes('submit') && !mayManage) || ((actions.includes('resolve') || actions.includes('reject')) && !mayDecide)) && (
                  <Notice tone="neutral">{t('w6.noPermission.action')}</Notice>
                )}
                {actionError !== undefined && <Notice tone="danger">{actionError}</Notice>}

                {open.resolution !== undefined && (
                  <section>
                    <SectionTitle level={3} size="sub">{t('fps.resolution.title')}</SectionTitle>
                    {(
                      [
                        ['fps.resolution.refund', open.resolution.refundToCustomer],
                        ['fps.resolution.retained', open.resolution.retainedFee],
                        ['fps.resolution.platform', open.resolution.platformFunded],
                        ['fps.resolution.merchant', open.resolution.merchantCharged],
                      ] as const
                    ).map(([id, amount]) => (
                      <Line key={id} caption>
                        <Ink tone="secondary">{t(id)}</Ink>
                        <span className="tnum">{money(amount)}</span>
                      </Line>
                    ))}
                    <Text as="p" role="caption" tone="secondary">{t('fps.resolution.version', { version: open.resolution.version })}</Text>
                  </section>
                )}

                {open.rejection !== undefined && <Notice tone="danger">{v('food.post_sale_decision_reason', open.rejection.reasonCode)}</Notice>}

                <section>
                  <SectionTitle level={3} size="sub">{t('sclaims.evidence.title')}</SectionTitle>
                  {open.evidence.length === 0 ? (
                    <Text as="p" role="caption" tone="secondary">{t('sclaims.evidence.empty')}</Text>
                  ) : (
                    open.evidence.map((e) => (
                      <Line key={e.evidenceId} caption>
                        <span>{v('food.postsale_evidence_type', e.type)}</span>
                        <Text role="caption" tone="secondary" mono>{formatMarketInstant(e.capturedAt, timeZone, lang)}</Text>
                      </Line>
                    ))
                  )}
                </section>
              </DrawerStack>
            </DrawerBody>
            <DrawerFooter>
              <Button variant="ghost" onClick={() => setOpen(undefined)}>{t('ui.close')}</Button>
              <span className="vello-grow" />
              {actions.includes('reject') && mayDecide && (
                <Button variant="danger" disabled={busy} onClick={() => void run(open, 'reject')}>{t('fps.action.reject')}</Button>
              )}
              {actions.includes('submit') && mayManage && (
                <Button variant="primary" disabled={busy} onClick={() => void run(open, 'submit')}>{t('fps.action.submit')}</Button>
              )}
              {actions.includes('resolve') && mayDecide && (
                <Button variant="primary" disabled={busy} onClick={() => void run(open, 'resolve')}>{t('fps.action.resolve')}</Button>
              )}
            </DrawerFooter>
          </>
        )}
      </Drawer>
    </>
  );
}
